<template>
  <div class="tw-flex tw-content-center tw-gap-1 tw-flex-col">
    <WdsStack :direction-column="!stackHorizontally" gap-sm>
      <WdsStack>
        <WdsTag
          v-if="shouldShowEngagementType"
          :warning="effective.engagementType === EngagementType.Pending"
          :sm="sm"
          subtle
          primary
        >
          {{ $t(engagementTypeLabel) }}
        </WdsTag>
        <WdsText v-else class="tw-text-center">-</WdsText>
      </WdsStack>
      <WdsText v-if="shouldShowEngagementTypeSetup" class="tw-content-center">
        <span class="tw-font-semibold">{{ $t(engagementTypeSetupLabel) }}</span>
      </WdsText>
    </WdsStack>
    <WdsTooltip v-if="supplierName" :text="`${$t('Via')} ${supplierName}`" global>
      <span class="tw-text-gray-500 tw-text-xs tw-cursor-help">{{ $t('Via') }} {{ supplierName }}</span>
    </WdsTooltip>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { WdsStack, WdsTag, WdsText, WdsTooltip } from '@worksome/wds-components'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { FragmentType, graphql, useFragment } from '@/gql'
import { EngagementType, EngagementTypeSetup } from '@/gql/graphql'

const HireEngagementColumn = graphql(`
  fragment HireEngagementColumn on Hire {
    engagementType
    engagementTypeLabel
    engagementTypeSetup
    engagementTypeSetupLabel
    supplierHire {
      id
      engagementType
      engagementTypeLabel
      engagementTypeSetup
      engagementTypeSetupLabel
      company {
        id
        name
      }
    }
  }
`)

const props = withDefaults(
  defineProps<{
    row: FragmentType<typeof HireEngagementColumn>
    stackHorizontally?: boolean
    sm?: boolean
  }>(),
  {
    stackHorizontally: false,
    sm: false,
  },
)

const { stackHorizontally } = props
const column = computed(() => useFragment(HireEngagementColumn, props.row))

// Prefer supplier hire's engagement type when available (shows the actual worker engagement)
const effective = computed(() => column.value.supplierHire ?? column.value)

const engagementTypeLabel = computed(() => effective.value.engagementTypeLabel)
const engagementTypeSetupLabel = computed(() => {
  const label = effective.value.engagementTypeSetupLabel
  return label ? label.replace(/\s*\([^)]*\)/g, '').trim() : label
})

const supplierName = computed(() => column.value.supplierHire?.company?.name)

const shouldShowEngagementType = computed(() => {
  if (!engagementTypeLabel.value) {
    return false
  }

  return true
})

const shouldShowEngagementTypeSetup = computed(() => {
  if (!engagementTypeSetupLabel.value) {
    return false
  }

  return (
    effective.value.engagementTypeSetup !== EngagementTypeSetup.Default &&
    effective.value.engagementTypeSetup !== EngagementTypeSetup.Pending
  )
})
</script>
